import { useRef, useState } from 'react'

import { useFocusOnMount } from '../learning/focus'
import {
  formatFormula,
  gradeAnswer,
  isAnswerComplete,
  normalizeFormula,
  simplerFraction,
} from './grade'
import { JourneyFrame, Note, SourceChip } from './JourneyChrome'
import {
  type Attempt,
  type ChoiceQuestion,
  HELP_LABELS,
  type HelpMode,
  type Question,
  type SampleMaterial,
  type Topic,
} from './model'
import { questionOriginLabel } from './report'
import { DEMO_TEXT, PRIMARY, SECONDARY } from './ui'

/**
 * Step 5: practice on the chosen topic, one question at a time. Each answer
 * kind has its own input — choice, English short answer, fraction, number with
 * unit, chemical formula — graded deterministically (`grade.ts`). The answer
 * is never shown before it is checked; "Kiểm tra" cannot submit twice.
 *
 * The four assistance modes (D-14.5) open pre-authored DEMO help. Any help
 * opened before checking is recorded with the attempt, and such answers are
 * not counted as independent evidence (FR-38, Exercise Framework §8). Full
 * Solution needs an explicit confirmation before the answer is checked.
 */

const FIELD =
  'h-14 w-full rounded-2xl border-2 border-slate-300 bg-white px-4 text-xl disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:disabled:bg-slate-800'

export interface PracticeStepProps {
  readonly sample: SampleMaterial
  readonly topic: Topic
  readonly index: number
  readonly attempts: ReadonlyMap<string, Attempt>
  /** Help opened before checking, by question ID. */
  readonly help: ReadonlyMap<string, readonly HelpMode[]>
  readonly onHelp: (questionId: string, mode: HelpMode) => void
  readonly onSubmitted: (attempt: Attempt) => void
  readonly onNext: () => void
  readonly onBack: () => void
  readonly onHome: () => void
}

export function PracticeStep({
  sample,
  topic,
  index,
  attempts,
  help,
  onHelp,
  onSubmitted,
  onNext,
  onBack,
  onHome,
}: PracticeStepProps): React.JSX.Element {
  const question = topic.questions[index]
  const total = topic.questions.length

  return (
    <JourneyFrame step="Luyện tập" title="Luyện tập" onBack={onBack} onHome={onHome} demo>
      <SourceChip sample={sample} />
      <div className="flex items-center gap-3">
        <div
          role="progressbar"
          aria-label="Tiến độ luyện tập"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={index + 1}
          aria-valuetext={`Câu ${String(index + 1)}/${String(total)}`}
          className="h-3 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
        >
          <div
            className="h-full rounded-full bg-emerald-600 dark:bg-emerald-400"
            style={{ width: `${String(((index + 1) / total) * 100)}%` }}
          />
        </div>
        <p className="text-base font-semibold text-slate-700 dark:text-slate-300">
          Câu {index + 1}/{total}
        </p>
      </div>

      {question !== undefined && (
        <QuestionCard
          key={question.id}
          sample={sample}
          question={question}
          attempt={attempts.get(question.id) ?? null}
          helpUsed={help.get(question.id) ?? []}
          isLast={index === total - 1}
          onHelp={(mode) => {
            onHelp(question.id, mode)
          }}
          onSubmitted={onSubmitted}
          onNext={onNext}
        />
      )}
    </JourneyFrame>
  )
}

function QuestionCard({
  sample,
  question,
  attempt,
  helpUsed,
  isLast,
  onHelp,
  onSubmitted,
  onNext,
}: {
  sample: SampleMaterial
  question: Question
  attempt: Attempt | null
  helpUsed: readonly HelpMode[]
  isLast: boolean
  onHelp: (mode: HelpMode) => void
  onSubmitted: (attempt: Attempt) => void
  onNext: () => void
}): React.JSX.Element {
  const [draft, setDraft] = useState(attempt?.answer ?? '')
  const submitted = useRef(attempt !== null)
  const locked = attempt !== null
  const complete = isAnswerComplete(question, draft)

  const submit = () => {
    if (submitted.current || !complete) return
    submitted.current = true
    const result = gradeAnswer(question, draft)
    onSubmitted({
      questionId: question.id,
      answer: draft,
      display: result.display,
      correct: result.correct,
      correctDisplay: result.correctDisplay,
      mistake: result.mistake,
      helpBeforeSubmit: helpUsed,
    })
  }

  return (
    <div className="flex flex-col gap-5">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <AnswerInput
          question={question}
          value={draft}
          locked={locked}
          onChange={setDraft}
        />
        {/* The label only, never the material's text (User Features UP-05). */}
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {questionOriginLabel(sample, question)}
        </p>
        {!locked && (
          <button
            type="submit"
            className={`${PRIMARY} md:self-center`}
            disabled={!complete}
          >
            Kiểm tra
          </button>
        )}
      </form>

      {attempt !== null && (
        <Feedback question={question} attempt={attempt} isLast={isLast} onNext={onNext} />
      )}

      <HelpPanel
        question={question}
        locked={locked}
        helpUsed={helpUsed}
        onHelp={onHelp}
      />
    </div>
  )
}

function AnswerInput({
  question,
  value,
  locked,
  onChange,
}: {
  question: Question
  value: string
  locked: boolean
  onChange: (value: string) => void
}): React.JSX.Element {
  const promptId = `prompt-${question.id}`
  const prompt = (
    <p
      id={promptId}
      lang={question.lang}
      className="text-2xl font-semibold wrap-break-word"
    >
      {question.prompt}
    </p>
  )

  switch (question.kind) {
    case 'choice':
      return (
        <ChoiceInput
          question={question}
          value={value}
          locked={locked}
          onChange={onChange}
        />
      )

    case 'text':
      return (
        <div className="flex flex-col gap-3">
          {prompt}
          <input
            aria-labelledby={promptId}
            lang="en"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className={FIELD}
            value={value}
            disabled={locked}
            placeholder="Gõ câu trả lời"
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
        </div>
      )

    case 'number':
      return (
        <div className="flex flex-col gap-3">
          {prompt}
          <div className="flex items-center gap-3">
            <input
              aria-labelledby={promptId}
              aria-describedby={
                question.unit === null ? undefined : `unit-${question.id}`
              }
              inputMode="decimal"
              autoComplete="off"
              className={`${FIELD} max-w-48 text-center`}
              value={value}
              disabled={locked}
              placeholder="?"
              onChange={(e) => {
                onChange(e.target.value)
              }}
            />
            {question.unit !== null && (
              <span id={`unit-${question.id}`} className="text-xl font-semibold">
                {question.unit}
              </span>
            )}
          </div>
          {value.trim() !== '' && !isAnswerComplete(question, value) && (
            <p className="text-base text-amber-800 dark:text-amber-300">
              Con nhập một số nhé, ví dụ 12 hoặc 0,5.
            </p>
          )}
        </div>
      )

    case 'fraction': {
      const [num = '', den = ''] = value.split('/')
      const set = (n: string, d: string) => {
        onChange(`${n.replace(/\D/g, '')}/${d.replace(/\D/g, '')}`)
      }
      const box = `${FIELD.replace('w-full', 'w-24')} text-center text-2xl font-semibold`
      return (
        <div className="flex flex-col gap-3">
          {prompt}
          <div
            className="flex items-center gap-4"
            role="group"
            aria-labelledby={promptId}
          >
            <span className="text-xl font-semibold" aria-hidden>
              =
            </span>
            <div className="flex flex-col items-center gap-1">
              <input
                aria-label="Tử số"
                inputMode="numeric"
                autoComplete="off"
                className={box}
                value={num}
                disabled={locked}
                onChange={(e) => {
                  set(e.target.value, den)
                }}
              />
              <span
                aria-hidden
                className="h-1 w-24 rounded bg-slate-800 dark:bg-slate-200"
              />
              <input
                aria-label="Mẫu số"
                inputMode="numeric"
                autoComplete="off"
                className={box}
                value={den}
                disabled={locked}
                onChange={(e) => {
                  set(num, e.target.value)
                }}
              />
            </div>
          </div>
          {den !== '' && Number(den) === 0 && (
            <p className="text-base text-amber-800 dark:text-amber-300">
              Mẫu số không thể bằng 0.
            </p>
          )}
        </div>
      )
    }

    case 'formula': {
      const preview = formatFormula(normalizeFormula(value))
      return (
        <div className="flex flex-col gap-3">
          {prompt}
          <input
            aria-labelledby={promptId}
            aria-describedby={`formula-tip-${question.id}`}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className={`${FIELD} font-mono`}
            value={value}
            disabled={locked}
            placeholder="Ví dụ: H2O"
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
          <p
            id={`formula-tip-${question.id}`}
            className="text-base text-slate-700 dark:text-slate-300"
          >
            Gõ chỉ số bằng số thường: H2O sẽ hiện là H₂O. Phân biệt chữ hoa, chữ thường.
          </p>
          {preview !== '' && (
            <p className="text-xl" aria-live="polite">
              Con viết: <span className="font-semibold">{preview}</span>
            </p>
          )}
        </div>
      )
    }
  }
}

function ChoiceInput({
  question,
  value,
  locked,
  onChange,
}: {
  question: ChoiceQuestion
  value: string
  locked: boolean
  onChange: (value: string) => void
}): React.JSX.Element {
  return (
    <fieldset className="flex min-w-0 flex-col gap-3">
      <legend
        lang={question.lang}
        className="mb-3 text-2xl font-semibold wrap-break-word"
      >
        {question.prompt}
      </legend>
      {question.options.map((option) => {
        const selected = value === option.id
        return (
          <label
            key={option.id}
            className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 py-3 text-lg font-medium has-[:disabled]:cursor-default has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-sky-600 ${
              selected
                ? 'border-sky-700 bg-sky-50 text-sky-950 dark:border-sky-400 dark:bg-sky-950 dark:text-sky-50'
                : 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900'
            }`}
          >
            <input
              type="radio"
              name={`choice-${question.id}`}
              value={option.id}
              checked={selected}
              disabled={locked}
              onChange={() => {
                onChange(option.id)
              }}
              className="sr-only"
            />
            <span lang={question.lang}>{option.text}</span>
            {selected && (
              <span className="ml-auto text-xl" aria-hidden>
                ✔
              </span>
            )}
          </label>
        )
      })}
    </fieldset>
  )
}

function Feedback({
  question,
  attempt,
  isLast,
  onNext,
}: {
  question: Question
  attempt: Attempt
  isLast: boolean
  onNext: () => void
}): React.JSX.Element {
  const next = useFocusOnMount<HTMLButtonElement>()
  const simpler = attempt.correct ? simplerFraction(question, attempt.answer) : null
  const assisted = attempt.helpBeforeSubmit.length > 0

  return (
    <section
      aria-labelledby={`feedback-${question.id}`}
      className={`flex flex-col gap-2 rounded-3xl border-2 p-4 ${
        attempt.correct
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
          : 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
      }`}
    >
      <h2 id={`feedback-${question.id}`} className="text-2xl font-bold">
        <span aria-hidden>{attempt.correct ? '🎉 ' : '💪 '}</span>
        {attempt.correct ? 'Đúng rồi!' : 'Chưa đúng'}
      </h2>
      <p className="text-lg">
        Con trả lời: <span className="font-semibold">{attempt.display}</span>
      </p>
      {!attempt.correct && (
        <p className="text-lg">
          Đáp án đúng: <span className="font-semibold">{attempt.correctDisplay}</span>
        </p>
      )}
      {attempt.mistake !== null && <p className="text-lg">{attempt.mistake.feedback}</p>}
      <p className="text-lg">
        <span className="font-semibold">Vì sao: </span>
        {question.explanation}
      </p>
      {simpler !== null && (
        <p className="text-base">Đúng! Có thể rút gọn thành {simpler}.</p>
      )}
      {assisted && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          Con đã mở trợ giúp trước khi trả lời, nên câu này được ghi là “có trợ giúp”.
        </p>
      )}
      <button
        ref={next}
        type="button"
        className={`${PRIMARY} mt-2 md:self-center`}
        onClick={onNext}
      >
        {isLast ? 'Xem kết quả' : 'Câu tiếp theo'}
      </button>
    </section>
  )
}

const HELP_MODES: readonly { mode: HelpMode; icon: string }[] = [
  { mode: 'explain', icon: '📘' },
  { mode: 'hint', icon: '💡' },
  { mode: 'guided', icon: '🪜' },
  { mode: 'full', icon: '📝' },
]

function HelpPanel({
  question,
  locked,
  helpUsed,
  onHelp,
}: {
  question: Question
  locked: boolean
  helpUsed: readonly HelpMode[]
  onHelp: (mode: HelpMode) => void
}): React.JSX.Element {
  const [open, setOpen] = useState<HelpMode | null>(null)
  const [confirmFull, setConfirmFull] = useState(false)
  const [guidedShown, setGuidedShown] = useState(0)
  const [guidedRevealed, setGuidedRevealed] = useState(false)
  const panelId = `help-${question.id}`

  const choose = (mode: HelpMode) => {
    if (open === mode) {
      setOpen(null)
      setConfirmFull(false)
      return
    }
    if (mode === 'full' && !locked && !helpUsed.includes('full')) {
      setOpen(null)
      setConfirmFull(true)
      return
    }
    setConfirmFull(false)
    setOpen(mode)
    if (!locked) onHelp(mode)
  }

  const step = question.help.guided[guidedShown]

  return (
    <section
      aria-labelledby={`help-title-${question.id}`}
      className="flex flex-col gap-3 rounded-3xl bg-slate-100 p-4 dark:bg-slate-900"
    >
      <h2 id={`help-title-${question.id}`} className="text-lg font-semibold">
        Cần trợ giúp?
      </h2>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {HELP_MODES.map(({ mode, icon }) => (
          <button
            key={mode}
            type="button"
            aria-expanded={open === mode}
            aria-controls={panelId}
            className="flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 bg-white px-2 py-1 text-center text-base leading-tight font-semibold aria-expanded:border-sky-700 aria-expanded:bg-sky-50 dark:border-slate-700 dark:bg-slate-950 dark:aria-expanded:border-sky-400 dark:aria-expanded:bg-sky-950"
            onClick={() => {
              choose(mode)
            }}
          >
            <span aria-hidden>{icon}</span>
            {HELP_LABELS[mode]}
          </button>
        ))}
      </div>

      {confirmFull && (
        <div
          role="alertdialog"
          aria-labelledby={`confirm-${question.id}`}
          className="flex flex-col gap-3 rounded-2xl bg-white p-4 dark:bg-slate-950"
        >
          <p id={`confirm-${question.id}`} className="text-lg">
            Xem lời giải đầy đủ sẽ cho con thấy đáp án. Câu này sẽ được ghi là “có trợ
            giúp”. Con vẫn muốn xem?
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                setConfirmFull(false)
                setOpen('full')
                onHelp('full')
              }}
            >
              Xem lời giải
            </button>
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                setConfirmFull(false)
              }}
            >
              Con tự làm tiếp
            </button>
          </div>
        </div>
      )}

      <div id={panelId} hidden={open === null} className="flex flex-col gap-2">
        {open === 'explain' && <p className="text-lg">{question.help.explain}</p>}
        {open === 'hint' && <p className="text-lg">{question.help.hint}</p>}
        {open === 'guided' && step !== undefined && (
          <div className="flex flex-col gap-2">
            <p className="text-base font-semibold text-slate-600 dark:text-slate-400">
              Bước {guidedShown + 1}/{question.help.guided.length}
            </p>
            <p className="text-lg">{step.prompt}</p>
            {guidedRevealed ? (
              <>
                <p className="rounded-xl bg-white px-3 py-2 text-lg font-semibold dark:bg-slate-950">
                  {step.reveal}
                </p>
                {guidedShown < question.help.guided.length - 1 ? (
                  <button
                    type="button"
                    className={`${SECONDARY} self-start`}
                    onClick={() => {
                      setGuidedShown((i) => i + 1)
                      setGuidedRevealed(false)
                    }}
                  >
                    Bước tiếp
                  </button>
                ) : (
                  <p className="text-base">
                    Xong các bước. Giờ con tự trả lời câu hỏi nhé.
                  </p>
                )}
              </>
            ) : (
              <button
                type="button"
                className={`${SECONDARY} self-start`}
                onClick={() => {
                  setGuidedRevealed(true)
                }}
              >
                Con làm xong, so kết quả
              </button>
            )}
          </div>
        )}
        {open === 'full' && (
          <ol className="flex flex-col gap-1">
            {question.help.full.map((line, i) => (
              <li key={line} className="text-lg">
                {i + 1}. {line}
              </li>
            ))}
          </ol>
        )}
        <Note>{DEMO_TEXT.help}</Note>
      </div>
      {!locked && helpUsed.length > 0 && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          Đã mở: {helpUsed.map((m) => HELP_LABELS[m]).join(', ')}. Câu này sẽ ghi là “có
          trợ giúp”.
        </p>
      )}
    </section>
  )
}
